import { OpenImgContextProvider } from 'openimg/react'
import { useEffect } from 'react'
import {
	data,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLoaderData,
} from 'react-router'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import { type Route } from './+types/root.ts'
import appleTouchIconAssetUrl from './assets/favicons/apple-touch-icon.png'
import faviconAssetUrl from './assets/favicons/favicon.svg'
import { GeneralErrorBoundary } from './components/error-boundary.tsx'
import { ImpersonationBanner } from './components/founders/impersonation-banner.tsx'
import { EpicProgress } from './components/progress-bar.tsx'
import { useToast } from './components/toaster.tsx'
import { href as iconsHref } from './components/ui/icon.tsx'
import { EpicToaster } from './components/ui/sonner.tsx'
import {
	useOptionalTheme,
	useTheme,
} from './routes/resources/theme-switch.tsx'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import { getUserId, logout } from './utils/auth.server.ts'
import { getImpersonationContext } from './utils/impersonation.server.ts'
import { ClientHintCheck, getHints } from './utils/client-hints.tsx'
import { prisma } from './utils/db.server.ts'
import { getEnv } from './utils/env.server.ts'
import { isLeaderboardEnabledForClasses } from './utils/gamification-settings.ts'
import { pipeHeaders } from './utils/headers.server.ts'
import { honeypot } from './utils/honeypot.server.ts'
import { combineHeaders, getDomainUrl, getImgSrc } from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import { type Theme, getTheme } from './utils/theme.server.ts'
import { makeTimings, time } from './utils/timing.server.ts'
import { getToast } from './utils/toast.server.ts'

export const links: Route.LinksFunction = () => {
	return [
		// Preload svg sprite as a resource to avoid render blocking
		{ rel: 'preload', href: iconsHref, as: 'image' },
		{
			rel: 'icon',
			href: '/favicon.ico',
			sizes: '48x48',
		},
		{ rel: 'icon', type: 'image/svg+xml', href: faviconAssetUrl },
		{ rel: 'apple-touch-icon', href: appleTouchIconAssetUrl },
		{
			rel: 'manifest',
			href: '/site.webmanifest',
			crossOrigin: 'use-credentials',
		} as const, // necessary to make typescript happy
		// Google Fonts: Press Start 2P (display) + Inter (body)
		{ rel: 'preconnect', href: 'https://fonts.googleapis.com' },
		{
			rel: 'preconnect',
			href: 'https://fonts.gstatic.com',
			crossOrigin: 'anonymous',
		} as const,
		{
			rel: 'stylesheet',
			href: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Press+Start+2P&display=swap',
		},
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
	].filter(Boolean)
}

export const meta: Route.MetaFunction = ({ data }) => {
	return [
		{ title: data ? 'Muziekdocent' : 'Fout | Muziekdocent' },
		{ name: 'description', content: 'Moderne muzieklessen voor het voortgezet onderwijs' },
	]
}

export async function loader({ request }: Route.LoaderArgs) {
	const timings = makeTimings('root loader')
	const userId = await time(() => getUserId(request), {
		timings,
		type: 'getUserId',
		desc: 'getUserId in root',
	})

	const user = userId
		? await time(
				() =>
					prisma.user.findUnique({
						select: {
							id: true,
							name: true,
							username: true,
							image: { select: { objectKey: true } },
							roles: {
								select: {
									name: true,
									permissions: {
										select: { entity: true, action: true, access: true },
									},
								},
							},
							studentProfile: {
								select: {
									xp: true,
									level: true,
									league: true,
									streakDays: true,
									enrollments: {
										select: {
											class: {
												select: {
													schoolLevel: true,
													gamificationSettings: true,
												},
											},
										},
									},
								},
							},
							teacherProfile: { select: { id: true } },
						},
						where: { id: userId },
					}),
				{ timings, type: 'find user', desc: 'find user in root' },
			)
		: null
	if (userId && !user) {
		console.info('something weird happened')
		// something weird happened... The user is authenticated but we can't find
		// them in the database. Maybe they were deleted? Let's log them out.
		await logout({ request, redirectTo: '/' })
	}
	// Narrow studentProfile to the client: compute leaderboardEnabled
	// server-side (ADR-102) and never ship the raw gamificationSettings
	// JSON or enrollment/class rows to the client.
	const userForClient = user
		? {
				...user,
				studentProfile: user.studentProfile
					? {
							xp: user.studentProfile.xp,
							level: user.studentProfile.level,
							league: user.studentProfile.league,
							streakDays: user.studentProfile.streakDays,
							leaderboardEnabled: isLeaderboardEnabledForClasses(
								user.studentProfile.enrollments.map(
									(enrollment) => enrollment.class,
								),
							),
						}
					: null,
			}
		: null

	const { toast, headers: toastHeaders } = await getToast(request)
	const honeyProps = await honeypot.getInputProps()

	// Impersonation banner data — null when not impersonating.
	const impersonation = await getImpersonationContext(request)
	let impersonationBanner: {
		impersonatedUserName: string
		startedAt: string
		writeModeUntil: string | null
		stopAction: string
	} | null = null
	if (impersonation) {
		const target = await prisma.user.findUnique({
			where: { id: impersonation.impersonatedUserId },
			select: {
				name: true,
				email: true,
				teacherProfile: { select: { schoolId: true } },
				studentProfile: { select: { schoolId: true } },
			},
		})
		const schoolId =
			target?.teacherProfile?.schoolId ?? target?.studentProfile?.schoolId
		impersonationBanner = {
			impersonatedUserName: target?.name ?? target?.email ?? 'onbekend',
			startedAt: impersonation.startedAt.toISOString(),
			writeModeUntil: impersonation.writeModeUntil?.toISOString() ?? null,
			stopAction: schoolId
				? `/founders/schools/${schoolId}/impersonate`
				: '/founders/schools',
		}
	}

	return data(
		{
			user: userForClient,
			impersonationBanner,
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				userPrefs: {
					theme: getTheme(request),
				},
			},
			ENV: getEnv(),
			toast,
			honeyProps,
		},
		{
			headers: combineHeaders(
				{ 'Server-Timing': timings.toString() },
				toastHeaders,
			),
		},
	)
}

export const headers: Route.HeadersFunction = pipeHeaders

function Document({
	children,
	nonce,
	theme = 'light',
	env = {},
}: {
	children: React.ReactNode
	nonce: string
	theme?: Theme
	env?: Record<string, string | undefined>
}) {
	const allowIndexing = ENV.ALLOW_INDEXING !== 'false'

	// Marks the moment React hydration has committed (this effect fires after
	// all descendants'). Until then the SSR HTML is visible but not
	// interactive: window-level key handlers aren't attached yet, and React's
	// hydration commit re-syncs form controls from server markup — wiping any
	// pre-hydration edits and collapsing text selections (see ADR-107). E2E
	// tests must gate on `html[data-hydrated]` (via `waitForHydration`) before
	// interacting.
	useEffect(() => {
		document.documentElement.dataset.hydrated = 'true'
	}, [])

	return (
		<html lang="nl" className={`${theme} h-full overflow-x-hidden`}>
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				{allowIndexing ? null : (
					<meta name="robots" content="noindex, nofollow" />
				)}
				<Links />
			</head>
			<body className="bg-background text-foreground">
				{children}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

export function Layout({ children }: { children: React.ReactNode }) {
	// if there was an error running the loader, data could be missing
	const data = useLoaderData<typeof loader | null>()
	const nonce = useNonce()
	const theme = useOptionalTheme()
	return (
		<Document nonce={nonce} theme={theme} env={data?.ENV}>
			{children}
		</Document>
	)
}

function App() {
	const data = useLoaderData<typeof loader>()
	const theme = useTheme()
	useToast(data.toast)

	return (
		<OpenImgContextProvider
			optimizerEndpoint="/resources/images"
			getSrc={getImgSrc}
		>
			{data.impersonationBanner ? (
				<ImpersonationBanner data={data.impersonationBanner} />
			) : null}
			<div className="flex min-h-screen flex-col">
				<Outlet />
			</div>
			<EpicToaster closeButton position="top-center" theme={theme} />
			<EpicProgress />
		</OpenImgContextProvider>
	)
}


function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	return (
		<HoneypotProvider {...data.honeyProps}>
			<App />
		</HoneypotProvider>
	)
}

export default AppWithProviders

// this is a last resort error boundary. There's not much useful information we
// can offer at this level.
export const ErrorBoundary = GeneralErrorBoundary
